import { CompanyContactId, Dtos, FEIN, fein, formatAddress, SuretyAccountId } from "@inrev/common";
import { type AdminAccountCompany, useAdminUpsertCompanyContact } from "@portal/api/account/admin";
import {
	CompanyNameFeinFields,
	ContactAddressField,
} from "@portal/components/features/contact-forms";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "@portal/components/form";
import { FormYesNo } from "@portal/components/form/fields";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button, Text } from "@portal/components/primitives";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useEffect, useMemo } from "react";
import { type FieldError, useController, useForm } from "react-hook-form";
import { z } from "zod";
import { useAddressValidation } from "../../../../api";
import { getSchemaDefaults, stripEmptyResolver, typedZodResolver } from "../../../../utils/form";
import { uniqueSchema } from "../../../../validation";

export const companyMustBeIncludedMessage =
	"Company must be included in either underwriting or indemnity";

export const getCompanyFormSchema = (feins: FEIN[]) =>
	Dtos.Admin.CompanyContact.Create.Request.schema
		.extend({
			fein: uniqueSchema(feins, fein.nullish(), "FEIN already used"),
		})
		.superRefine((val, ctx) => {
			if (val.includeInIndemnity === false && val.includeInUnderwriting === false) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["inclusion"],
					message: companyMustBeIncludedMessage,
				});
			}
		});

type CompanyFormData = z.infer<ReturnType<typeof getCompanyFormSchema>>;

// The address field holds the raw string in RHF; the resolver validates and transforms
// it into a `ValidAddress` on submit, so the input and output types diverge.
type CompanyFormInput = Omit<CompanyFormData, "address"> & {
	address: string | undefined;
	inclusion?: undefined;
};

type AdminCompanyModalProps = {
	accountId: SuretyAccountId;
	companies: AdminAccountCompany[];
	selectedCompanyId?: CompanyContactId;
	onClose: () => void;
};

export const AdminCompanyModal = ({
	accountId,
	companies,
	selectedCompanyId,
	onClose,
}: AdminCompanyModalProps) => {
	const { upsertCompanyContact, upsertCompanyContactIsLoading, upsertCompanyContactSuccess } =
		useAdminUpsertCompanyContact(accountId, selectedCompanyId);

	const selectedCompany =
		selectedCompanyId !== undefined
			? companies.find((company) => company.id === selectedCompanyId)
			: undefined;

	const feins = useMemo(
		() =>
			companies.filter((c) => c.id !== selectedCompanyId && !!c.fein).map((c) => c.fein) as FEIN[],
		[companies, selectedCompanyId],
	);

	const companyFormSchema = useMemo(() => getCompanyFormSchema(feins), [feins]);

	const formMethods = useForm<CompanyFormInput, any, CompanyFormData>({
		mode: "onBlur",
		resolver: stripEmptyResolver(typedZodResolver(companyFormSchema)),
	});

	useEffect(() => {
		formMethods.reset(
			{
				...getSchemaDefaults(Dtos.Admin.CompanyContact.Create.Request.schema),
				address: "",
				...(selectedCompany
					? {
							name: selectedCompany.name,
							fein: selectedCompany.fein || null,
							address: selectedCompany.address ? formatAddress(selectedCompany.address) : "",
							includeInUnderwriting: selectedCompany.config.contract.includeInUnderwriting,
							includeInIndemnity: selectedCompany.config.contract.includeInIndemnity,
						}
					: {}),
			},
			{ keepDefaultValues: true },
		);
	}, [selectedCompany]);

	const includeInUnderwriting = formMethods.watch("includeInUnderwriting");
	const includeInIndemnity = formMethods.watch("includeInIndemnity");
	const inclusionError = formMethods.formState.errors.inclusion?.message;
	const showInclusionError =
		inclusionError !== undefined && !includeInUnderwriting && !includeInIndemnity;

	const addressController = useController({ name: "address", control: formMethods.control });

	const { addressFieldControl } = useAddressValidation({
		value: formMethods.watch("address"),
		error: addressController.fieldState.error,
		setValue: (value: string) => formMethods.setValue("address", value),
		setError: (error: FieldError) => formMethods.setError("address", error),
		clearError: () => formMethods.clearErrors("address"),
	});

	const handleSubmit = (formData: CompanyFormData) => {
		upsertCompanyContact(formData);
	};

	useEffect(() => {
		if (upsertCompanyContactSuccess) onClose();
	}, [upsertCompanyContactSuccess]);

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header={selectedCompanyId !== undefined ? "Update Company" : "New Company"}
				onClose={onClose}
			>
				<div className="max-w-[820px] min-w-[747px] px-[40px] pt-[25px] pb-[35px]">
					<FormSchemaProvider value={companyFormSchema}>
						<form className="w-full h-fit flex flex-col space-y-[25px]">
							<FormFieldGroup condensed>
								{showInclusionError && (
									<Text type="caption" color="danger">
										{inclusionError}
									</Text>
								)}
								<FormRow className="justify-start gap-x-[40px]">
									<FormField className="max-w-fit">
										<FormItemLabel condensed name="includeInUnderwriting">
											Include in underwriting?
										</FormItemLabel>
										<FormYesNo
											name="includeInUnderwriting"
											control={formMethods.control}
											compact
											rules={{ deps: "includeInIndemnity" }}
										/>
									</FormField>
									<FormField className="max-w-fit">
										<FormItemLabel condensed name="includeInIndemnity">
											Include in indemnity?
										</FormItemLabel>
										<FormYesNo
											name="includeInIndemnity"
											control={formMethods.control}
											compact
											rules={{ deps: "includeInUnderwriting" }}
										/>
									</FormField>
								</FormRow>
								<CompanyNameFeinFields formMethods={formMethods} />
								<ContactAddressField
									formMethods={formMethods}
									addressControl={addressFieldControl}
									compact
								/>
							</FormFieldGroup>
							<div className="w-full flex justify-center mt-[5px]">
								<Button
									onClick={formMethods.handleSubmit(handleSubmit)}
									color="primary"
									className="w-[150px] h-[36px]"
									tabIndex={2}
									loading={formMethods.formState.isSubmitting || upsertCompanyContactIsLoading}
								>
									Submit
								</Button>
							</div>
						</form>
					</FormSchemaProvider>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
